import { Building2, Truck, CalendarClock, Package } from 'lucide-react';
import CampoNumero from './CampoNumero';
import { formatarMoeda, formatarData, formatarCnpj, REGIMES } from './formato';

function Info({ rotulo, children }) {
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{rotulo}</p>
      <div className="text-sm text-gray-900">{children}</div>
    </div>
  );
}

/** Dados da nota como um todo: emitente, loja/regime, frete, volumes e duplicatas. */
export default function CabecalhoNota({ nota, loja, regime, onRegime, aliquotaInternaCE, onAliquotaInternaCE, ia }) {
  const modalidadeIa = ia?.modalidade_frete;
  const fob = nota.freteFicaComALoja;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs text-gray-500">NF-e {nota.numero} · série {nota.serie} · emitida em {formatarData(nota.dataEmissao)}</p>
          <h2 className="text-lg font-semibold text-gray-900">{nota.emitente.nome}</h2>
          <p className="text-xs text-gray-500 break-all">Chave {nota.chave}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-500">Valor total da nota</p>
          <p className="text-2xl font-bold text-gray-900">{formatarMoeda(nota.totais.vNF)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Info rotulo="Emitente">
          <span className="flex items-center gap-1"><Building2 className="w-4 h-4 text-gray-400" />{formatarCnpj(nota.emitente.cnpj)}</span>
          <span className="inline-block mt-1 px-2 py-0.5 rounded bg-gray-100 text-xs font-semibold">UF {nota.emitente.uf || '—'}</span>
        </Info>

        <Info rotulo="Loja / regime tributário">
          <p className="truncate flex items-center gap-2">
            {String(loja?.nome || nota.destinatario.nome || '—').replace(/[eé]lida\s+eletro/gi, 'Dantas Eletro')}
            {loja?.regime && regime === loja.regime && (
              <span
                className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 text-[10px] font-semibold uppercase"
                title="Regime escolhido automaticamente pela loja destinatária da nota"
              >
                automático
              </span>
            )}
          </p>
          <select
            value={regime}
            onChange={e => onRegime(e.target.value)}
            className={`mt-1 w-full px-2 py-1 text-sm border rounded-md ${regime ? 'border-gray-300' : 'border-red-400 bg-red-50'}`}
          >
            <option value="">Escolha o regime…</option>
            {REGIMES.map(r => <option key={r.valor} value={r.valor}>{r.label}</option>)}
          </select>
        </Info>

        <Info rotulo="Frete">
          <span className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-gray-400" />
            <span className={`px-2 py-0.5 rounded text-xs font-bold ${fob ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
              {fob ? 'FOB' : 'CIF'}
            </span>
            {modalidadeIa && (
              <span className="text-xs text-gray-500" title="Leitura da IA">IA: {modalidadeIa}</span>
            )}
          </span>
          <p className="text-xs text-gray-500 mt-1">{nota.modFreteDescricao}</p>
        </Info>

        <Info rotulo="Peso e volumes">
          <span className="flex items-center gap-1"><Package className="w-4 h-4 text-gray-400" />
            {nota.pesoBruto ? `${nota.pesoBruto.toLocaleString('pt-BR')} kg brutos` : 'Peso não informado'}
          </span>
          <p className="text-xs text-gray-500">{nota.volumes || 0} volume(s)</p>
        </Info>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 pt-3 border-t border-gray-100">
        <div className="lg:col-span-3">
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500 mb-1 flex items-center gap-1">
            <CalendarClock className="w-3.5 h-3.5" /> Duplicatas (prazo de pagamento)
          </p>
          {nota.duplicatas.length === 0 ? (
            <p className="text-sm text-gray-500">Sem duplicatas na nota (pagamento à vista ou fora da NF-e).</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {nota.duplicatas.map(d => (
                <span key={`${d.numero}-${d.vencimento}`} className="px-2 py-1 rounded-md bg-gray-50 border border-gray-200 text-xs">
                  {d.numero} · {formatarData(d.vencimento)} · <strong>{formatarMoeda(d.valor)}</strong>
                </span>
              ))}
              {nota.prazoMedioDias != null && (
                <span className="px-2 py-1 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-800">
                  Prazo médio: <strong>{nota.prazoMedioDias} dias</strong>
                </span>
              )}
            </div>
          )}
        </div>
        <CampoNumero
          label="Alíquota interna do ICMS no CE (%)"
          valor={aliquotaInternaCE}
          onChange={onAliquotaInternaCE}
          minimo={0}
          titulo="Usada no cálculo da antecipação / diferença de alíquota"
        />
      </div>

      {ia?.alertas_gerais?.length > 0 && (
        <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 list-disc list-inside space-y-0.5">
          {ia.alertas_gerais.map(a => <li key={a}>{a}</li>)}
        </ul>
      )}
    </div>
  );
}
